import React from 'react';
import { FaShieldAlt, FaUsers, FaAward, FaHeart } from 'react-icons/fa';
import SEO from '../components/SEO';
import { generateJSONLD } from '../utils/seo';

const About: React.FC = () => {
  const schema = generateJSONLD({
    type: 'Organization',
    name: 'GameObsidian',
    url: 'https://gameobsidian.com',
    logo: 'https://gameobsidian.com/images/logo.webp',
    description: 'Comprehensive UK casino offer reviews and comparisons',
    foundingDate: '2020',
    sameAs: [
      'https://facebook.com/gameobsidian',
      'https://twitter.com/gameobsidian',
    ],
  });

  const values = [
    {
      icon: FaShieldAlt,
      title: 'Transparency',
      description: 'We provide honest, unbiased reviews with no hidden agendas',
    },
    {
      icon: FaUsers,
      title: 'Player-First',
      description: 'Your safety and satisfaction are our top priorities',
    },
    {
      icon: FaAward,
      title: 'Excellence',
      description: 'We only review licensed, reputable UK casinos',
    },
    {
      icon: FaHeart,
      title: 'Responsible Gaming',
      description: 'We promote safe and responsible gambling practices',
    },
  ];

  return (
    <>
      <SEO
        title="About Us - GameObsidian"
        description="Learn about GameObsidian, your trusted source for comprehensive UK casino offer reviews. We provide honest, detailed analysis to help you make informed decisions."
        url="/about"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: schema }}
      />

      <div className="min-h-screen pt-20 pb-20">
        <div className="container mx-auto px-4">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              About <span className="text-gradient">GameObsidian</span>
            </h1>
            <p className="text-xl text-dark-muted max-w-3xl mx-auto">
              Your trusted partner in finding the best UK casino offers
            </p>
          </div>

          {/* Mission Section */}
          <div className="glass-effect rounded-2xl p-8 md:p-12 mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Our Mission
            </h2>
            <p className="text-lg text-dark-muted leading-relaxed mb-4">
              At GameObsidian, we're dedicated to providing comprehensive, honest, and detailed reviews of UK casino offers. 
              Our mission is to empower players with the information they need to make informed decisions about where to play.
            </p>
            <p className="text-lg text-dark-muted leading-relaxed">
              We understand that choosing the right casino can be overwhelming with so many options available. 
              That's why we do the research for you, testing each casino's bonuses, games, customer service, and overall experience 
              to bring you accurate, up-to-date information.
            </p>
          </div>

          {/* Values Section */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
              Our <span className="text-gradient">Values</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div
                  key={index}
                  className="glass-effect rounded-xl p-6 text-center hover:scale-105 transition-transform"
                >
                  <div className="bg-accent-primary/20 p-4 rounded-full inline-flex mb-4">
                    <value.icon className="text-3xl text-accent-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{value.title}</h3>
                  <p className="text-dark-muted">{value.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* What We Do Section */}
          <div className="glass-effect rounded-2xl p-8 md:p-12 mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              What We Do
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="bg-accent-primary/20 p-2 rounded-lg flex-shrink-0">
                  <div className="w-2 h-2 bg-accent-primary rounded-full" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Comprehensive Reviews</h3>
                  <p className="text-dark-muted">
                    We thoroughly test each casino, examining bonuses, game selection, payment methods, 
                    customer support, and user experience to provide you with complete insights.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-accent-primary/20 p-2 rounded-lg flex-shrink-0">
                  <div className="w-2 h-2 bg-accent-primary rounded-full" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Side-by-Side Comparisons</h3>
                  <p className="text-dark-muted">
                    Our comparison tools help you quickly identify the differences between casinos, 
                    making it easier to find the perfect match for your preferences.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-accent-primary/20 p-2 rounded-lg flex-shrink-0">
                  <div className="w-2 h-2 bg-accent-primary rounded-full" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Exclusive Offers</h3>
                  <p className="text-dark-muted">
                    We partner with top UK casinos to bring you exclusive bonuses and promotions 
                    that you won't find anywhere else.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-accent-primary/20 p-2 rounded-lg flex-shrink-0">
                  <div className="w-2 h-2 bg-accent-primary rounded-full" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Regular Updates</h3>
                  <p className="text-dark-muted">
                    The casino industry changes rapidly. We continuously update our reviews to ensure 
                    you always have access to the latest information and offers.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Commitment Section */}
          <div className="glass-effect rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Our Commitment to You
            </h2>
            <p className="text-lg text-dark-muted leading-relaxed mb-4">
              We are committed to maintaining the highest standards of integrity and transparency. 
              All our reviews are based on thorough research and real player experiences. 
              We only recommend casinos that are licensed by the UK Gambling Commission and meet our strict quality criteria.
            </p>
            <p className="text-lg text-dark-muted leading-relaxed">
              Responsible gambling is at the heart of everything we do. We provide resources and information 
              to help you gamble safely and responsibly. If you ever feel that gambling is becoming a problem, 
              please seek help from organizations like BeGambleAware or GamCare.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;

